/** The look an embedded page was handed, and what of it may be used.
 *
 * The values arrive as query parameters from the host script, which means
 * anyone who can write a URL can propose them — including on our own hosted
 * ad pages. So every one is validated here before it reaches a stylesheet:
 * colours must be colours, a font stack must read like a font stack, and a
 * font stylesheet must come from a font host. Anything else is dropped and
 * the page keeps its own default.
 */

/** Hex, rgb()/rgba(), hsl()/hsla() and the bare CSS colour keywords. Nothing
 *  with a semicolon, a brace, a url() or a var() gets through. */
const COLOUR_RE =
	/^(#[0-9a-f]{3,8}|rgba?\([\d\s.,%/]+\)|hsla?\([\d\s.,%/deg]+\)|[a-z]{3,20})$/i;

/** Family names, quotes, spaces and commas — the shape of a font stack. */
const FONT_RE = /^[\w\s,'"()-]{1,200}$/;

const FONT_CSS_HOSTS = ["fonts.googleapis.com", "use.typekit.net"];

export interface Look {
	font: string;
	text: string;
	bg: string;
	accent: string;
	accentText: string;
	fontCss: string;
	/** A self-hosted font the page carries, to re-declare inside the frame. */
	fontFamily: string;
	fontFile: string;
}

const EMPTY: Look = {
	font: "",
	text: "",
	bg: "",
	accent: "",
	accentText: "",
	fontCss: "",
	fontFamily: "",
	fontFile: "",
};

/** A font file address, from anywhere but over https and shaped like a font.
 *
 * A self-hosted font lives on the customer's own site, so there is no host to
 * allow-list; the extension and the scheme are what can be checked. */
function fontFile(value: string | null): string {
	if (!value) return "";
	try {
		const url = new URL(value);
		const looksLikeAFont = /\.(woff2?|ttf|otf)($|\?)/i.test(
			url.pathname + url.search
		);
		// https, unless this page is itself on http — a dev server or a preview
		// build, where an https-only rule would drop every local font.
		const secure =
			url.protocol === "https:" || window.location.protocol === "http:";
		return secure && looksLikeAFont ? url.toString() : "";
	} catch {
		return "";
	}
}

function colour(value: string | null): string {
	const trimmed = (value ?? "").trim();
	return COLOUR_RE.test(trimmed) ? trimmed : "";
}

function fontStack(value: string | null): string {
	const trimmed = (value ?? "").trim();
	return FONT_RE.test(trimmed) ? trimmed : "";
}

function fontStylesheet(value: string | null): string {
	if (!value) return "";
	try {
		const url = new URL(value);
		const allowed =
			url.protocol === "https:" && FONT_CSS_HOSTS.includes(url.hostname);
		return allowed ? url.toString() : "";
	} catch {
		return "";
	}
}

/** Text that stays readable on ``background``.
 *
 * The accent is the host page's link colour, and the ad writes its own label
 * on top of it — so the pair has to be decided here rather than measured.
 * Relative luminance per WCAG, on the sRGB channels we can parse. */
export function readableOn(background: string): string {
	const channels = background.match(/\d+(\.\d+)?/g);
	if (!channels || channels.length < 3) return "";
	const [red, green, blue] = channels.slice(0, 3).map(Number);
	const luminance = (0.299 * red + 0.587 * green + 0.114 * blue) / 255;
	return luminance > 0.6 ? "#1a1a1a" : "#ffffff";
}

/** The look from a query string, with everything unusable dropped. */
export function lookFromParams(params: URLSearchParams): Look {
	const accent = colour(params.get("accent"));
	return {
		...EMPTY,
		font: fontStack(params.get("font")),
		text: colour(params.get("text")),
		bg: colour(params.get("bg")),
		accent,
		accentText: accent ? readableOn(accent) : "",
		fontCss: fontStylesheet(params.get("fontCss")),
		fontFamily: fontStack(params.get("fontFamily")),
		fontFile: fontFile(params.get("fontFile")),
	};
}
